import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Map, List } from 'immutable';
import { capitalize } from 'lodash';

import { AddOns } from 'models/addons';
import { Location } from 'models/locations';
import { Fee } from 'models/fees';
import Quote from 'models/quote';
import Search from 'models/search';

import CouponCode from 'components/checkout/review-and-confirm/coupon-code';
import CheckoutSummaryLineItem from 'components/common/checkout-summary-line-item';
import CheckoutSummaryFeeItem from 'components/common/checkout-summary-fee-item';

import BrandName from 'containers/common/brand-name';

import { ExperimentsConsumer } from 'providers/experiments-provider';

const propTypes = {
  addOns: PropTypes.oneOfType([
    PropTypes.instanceOf(AddOns),
    PropTypes.instanceOf(Map),
  ]),
  fees: PropTypes.instanceOf(List).isRequired,
  currentSearch: PropTypes.instanceOf(Search),
  isFullTransparency: PropTypes.bool,
  isCheckoutSurprise: PropTypes.bool,
  location: PropTypes.instanceOf(Location),
  quote: PropTypes.instanceOf(Quote).isRequired,
  total: PropTypes.number,
  userCreditAmount: PropTypes.number,
};

const defaultProps = {
  addOns: new AddOns(),
  fees: List(),
  currentSearch: new Search(),
  isFullTransparency: false,
  isCheckoutSurprise: false,
  location: new Location(),
  total: 0,
  userCreditAmount: 0,
};

export class CheckoutSummary extends Component {
  get feeCategories() {
    const { fees } = this.props;
    const groups = {};
    fees.forEach((fee) => {
      if (!groups[fee.category]) {
        groups[fee.category] = [];
      }
      groups[fee.category].push(fee);
    });
    return groups;
  }

  renderAddOns() {
    const { addOns, location } = this.props;

    const renderableAddOns = [];

    addOns.forEach((selectedAddOns) => {
      if (!selectedAddOns.isEmpty()) {
        selectedAddOns.forEach((selectedAddOn) => {
          renderableAddOns.push(
            <CheckoutSummaryLineItem
              key={selectedAddOn.id}
              label={capitalize(selectedAddOn.name)}
              location={location}
              opacity="50"
              value={selectedAddOn.formattedPrice}
            />,
          );
        });
      }
    });

    return renderableAddOns;
  }

  renderFees() {
    const { location } = this.props;
    const { feeCategories } = this;

    const breakdown = [];

    for (const [category, fees] of Object.entries(feeCategories)) {
      breakdown.push(
        <CheckoutSummaryFeeItem
          category={category}
          fees={fees}
          key={category}
          location={location}
        />
      );
    }
  
    return breakdown;
  }

  renderPaymentSummary() {
    const { location, quote } = this.props;

    return (
      <Fragment>
        <div className="row">
          <div className="col-xs-12">
            <h2 className="text-color-dark-slate text-size-20 text-weight-bold margin-0">Payment Summary</h2>
          </div>
        </div>
        <CheckoutSummaryLineItem
          label="Base Price"
          location={location}
          opacity="50"
          value={quote.basePrice}
        />
        {this.renderAddOns()}
        {this.renderFees()}
        {this.renderUserCredit()}
        <div className="margin-top-sm-20 margin-bottom-sm-20 margin-top-xs-10 margin-bottom-xs-20">
          <CouponCode />
        </div>
      </Fragment>
    );
  }

  renderUserCredit() {
    const { location, userCreditAmount } = this.props;

    if (userCreditAmount <= 0) return null;

    const label =
      (<Fragment>
        <BrandName /> Credits
      </Fragment>);

    return (
      <CheckoutSummaryLineItem
        label={label}
        location={location}
        opacity="50"
        value={userCreditAmount}
        valuePrefix="-"
      />
    );
  }

  render() {
    const { location, total } = this.props;

    return (
      <Fragment>
        {this.renderPaymentSummary()}
        <CheckoutSummaryLineItem
          label="Your Order Total"
          value={total}
          location={location}
          isTotalRow
        />
      </Fragment>
    );
  }
}

CheckoutSummary.propTypes = propTypes;
CheckoutSummary.defaultProps = defaultProps;

const CheckoutSummaryWrapper = props => (
  <ExperimentsConsumer>
    <CheckoutSummary {...props} />
  </ExperimentsConsumer>
);

export default CheckoutSummaryWrapper;
